import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { AdminLayout } from "./AdminLayout";

const TABS = [
  { key: "", label: "All" },
  { key: "booking", label: "Bookings" },
  { key: "contact", label: "Messages" },
];

export default function AdminSubmissions() {
  const [tab, setTab] = useState("");
  const [detail, setDetail] = useState(null);
  const { data: styles = [] } = useQuery({ queryKey: ["styles"], queryFn: async () => (await api.get("/styles")).data });
  const { data: items = [], isLoading } = useQuery({ queryKey: ["submissions"], queryFn: async () => (await api.get("/admin/submissions")).data });
  const styleMap = Object.fromEntries(styles.map((s) => [s.slug, s.name]));

  const filtered = useMemo(() => items.filter((i) => !tab || i.type === tab), [items, tab]);

  return (
    <AdminLayout title="Submissions">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`submissions-tab-${t.label.toLowerCase()}`} onClick={() => setTab(t.key)}
            className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] px-4 py-2 border transition-colors ${tab === t.key ? "bg-ember text-obsidian border-ember" : "border-ash text-[#C4C4CC] hover:border-white"}`}>
            {t.label}
          </button>
        ))}
        <span className="ml-auto font-mono text-xs uppercase tracking-widest text-[#828290]" data-testid="submissions-count">{filtered.length} total</span>
      </div>

      {isLoading ? <p className="text-[#828290] font-mono text-sm">Loading…</p> : filtered.length === 0 ? (
        <div className="border border-ash p-16 text-center text-[#828290]">No submissions yet.</div>
      ) : (
        <div className="border border-ash overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ash text-left font-mono text-[0.6rem] uppercase tracking-widest text-[#828290]">
                <th className="p-4">Type</th><th className="p-4">Client</th>
                <th className="p-4 hidden md:table-cell">Artist</th><th className="p-4 hidden lg:table-cell">Style</th>
                <th className="p-4 hidden lg:table-cell">Date</th><th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={`${it.type}-${it.id}`} data-testid={`submission-row-${it.id}`} className="border-b border-ash hover:bg-charcoal transition-colors">
                  <td className="p-4"><span className={`inline-block font-mono text-[0.55rem] uppercase tracking-widest border px-2 py-1 ${it.type === "booking" ? "text-ember border-ember" : "text-blue-400 border-blue-400/60"}`}>{it.type === "booking" ? "Booking" : "Message"}</span></td>
                  <td className="p-4"><div className="text-white">{it.name}</div><div className="text-[#828290] text-xs">{it.email}</div></td>
                  <td className="p-4 hidden md:table-cell text-[#C4C4CC]" data-testid={`submission-artist-${it.id}`}>{it.artist || "—"}</td>
                  <td className="p-4 hidden lg:table-cell text-[#C4C4CC]">{styleMap[it.style] || it.style || "—"}</td>
                  <td className="p-4 hidden lg:table-cell text-[#828290] font-mono text-xs">{it.created_at ? new Date(it.created_at).toLocaleDateString() : "—"}</td>
                  <td className="p-4 text-right"><button data-testid={`submission-view-${it.id}`} onClick={() => setDetail({ ...it, styleName: styleMap[it.style] || it.style })} className="font-mono text-[0.65rem] uppercase tracking-widest text-ember hover:text-white">View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-[80] flex justify-end" data-testid="submission-detail">
          <div className="absolute inset-0 bg-obsidian/80 backdrop-blur-sm" onClick={() => setDetail(null)} />
          <div className="relative w-full max-w-lg bg-charcoal border-l border-ash h-full overflow-y-auto p-8">
            <p className="eyebrow mb-2">{detail.type === "booking" ? "Booking" : "Message"}</p>
            <h2 className="font-serif text-4xl text-white mb-6">{detail.name}</h2>
            <dl className="space-y-4 text-sm">
              <Row k="Email" v={detail.email} />
              {detail.phone && <Row k="Phone" v={detail.phone} />}
              {detail.type === "booking" && <Row k="Artist" v={detail.artist || "—"} />}
              {detail.type === "booking" && <Row k="Style" v={detail.styleName || "—"} />}
              {detail.status && <Row k="Status" v={detail.status} />}
              <Row k="Received" v={detail.created_at ? new Date(detail.created_at).toLocaleString() : "—"} />
            </dl>
            <div className="mt-6">
              <p className="eyebrow mb-2">{detail.type === "booking" ? "Details" : "Message"}</p>
              <p className="text-[#C4C4CC] leading-relaxed whitespace-pre-wrap">{detail.message || "—"}</p>
            </div>
            <button onClick={() => setDetail(null)} className="mt-8 font-mono text-[0.65rem] uppercase tracking-widest text-[#828290] hover:text-white">Close</button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 border-b border-ash pb-3">
      <dt className="font-mono text-[0.6rem] uppercase tracking-widest text-[#828290] shrink-0">{k}</dt>
      <dd className="text-white text-right break-all">{v}</dd>
    </div>
  );
}
